// Generated by GitHub Copilot
import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Alert,
  Toast,
  ToastContainer,
  Spinner,
} from "react-bootstrap";
import { Search, RotateCcw, Info } from "react-feather";
import SelfServiceFeatureToggle from "./SelfServiceFeatureToggle";
import { setMyFeatureFlag, retryApiCall } from "../../connections/featureFlags";
import { getFeatureToggleDefinitions } from "../../connections/featureDefinitions";
import usePolling from '../../hooks/usePolling';
import { POLLING_INTERVALS } from '../../config/polling';
import {
  trackProfileView,
  trackProfileFeatureToggle,
  trackProfileFeatureToggleError,
  trackProfileFeatureReset,
  trackProfileFeatureSearch
} from '../../utils/modernUITracking';

/**
 * ProfileFeatureManager Component
 *
 * Manages feature flags for the current user's profile.
 * Updated for US1717644 "Even Muggles Get Toggles" - accessible to all authenticated users for self-service.
 * Updated for US1731183 "Feature Toggle List: Fetch from API" - now fetches definitions from API instead of hardcoded registry.
 */
function ProfileFeatureManager({ user, onRefreshStateChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [toasts, setToasts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const initializeFeatures = async (isBackgroundRefresh = false) => {
    try {
      if (isBackgroundRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch feature definitions from API
      const featureDefinitions = await getFeatureToggleDefinitions();

      // Combine API definitions with user's current feature state
      const features = featureDefinitions.map((featureConfig) => {
        const isEnabled = user?.features?.[featureConfig.name] === true;

        return {
          ...featureConfig,
          enabled: isEnabled,
        };
      });

      setAvailableFeatures(features);
      setLastUpdated(new Date());
      setIsInitialLoad(false);
    } catch (err) {
      if (isBackgroundRefresh) {
      } else {
        setError(
          `Failed to load feature flags: ${err.message}. Please refresh the page and try again.`
        );
      }
    } finally {
      if (isBackgroundRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    initializeFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Track page view on mount (after features loaded)
  useEffect(() => {
    if (!loading && !error && availableFeatures.length > 0) {
      const enabledFeatures = availableFeatures.filter(f => f.enabled);
      trackProfileView({
        totalFeatures: availableFeatures.length,
        enabledFeatures: enabledFeatures.length,
        enabledFeatureNames: enabledFeatures.map(f => f.name)
      });
    }
  }, [loading, error, availableFeatures]);

  // Notify parent of refresh state changes
  useEffect(() => {
    if (onRefreshStateChange) {
      onRefreshStateChange({ refreshing, lastUpdated });
    }
  }, [refreshing, lastUpdated, onRefreshStateChange]);

  // Background auto-refresh
  usePolling(
    () => initializeFeatures(true),
    POLLING_INTERVALS.PROFILE_FEATURES,
    {
      enabled: !isInitialLoad && !loading,
      pauseWhenHidden: true
    }
  );

  /**
   * Handle feature flag toggle
   */
  // Generated by GitHub Copilot
  const handleFeatureToggle = async (featureKey, newValue) => {
    // Find the current feature state for rollback
    const currentFeature = availableFeatures.find((f) => f.name === featureKey);
    const originalValue = currentFeature ? currentFeature.enabled : false;

    // Optimistic update - update the availableFeatures array
    setAvailableFeatures((prev) =>
      prev.map((feature) =>
        feature.name === featureKey
          ? { ...feature, enabled: newValue }
          : feature
      )
    );

    try {
      // Call the real API with retry mechanism
      await retryApiCall(() =>
        setMyFeatureFlag(featureKey, newValue)
      );

      // Update user context if available
      if (user && typeof user.updateFeatures === "function") {
        user.updateFeatures({ [featureKey]: newValue });
      }

      // Track successful toggle
      trackProfileFeatureToggle(featureKey, newValue, {
        featureDisplayName: currentFeature?.displayName || featureKey,
        category: currentFeature?.category || 'unknown',
        isExperimental: currentFeature?.experimental || false
      });

    } catch (error) {
      // Rollback optimistic update on failure
      setAvailableFeatures((prev) =>
        prev.map((feature) =>
          feature.name === featureKey
            ? { ...feature, enabled: originalValue }
            : feature
        )
      );

      // Enhanced error handling for security-related errors
      const currentFeature = availableFeatures.find(
        (f) => f.name === featureKey
      );
      let errorTitle = "Update Failed";
      let errorMessage = `Failed to update ${
        currentFeature?.displayName || featureKey
      }`;
      let errorType = "unknown";

      if (error.message.includes("Rate limit")) {
        errorTitle = "Rate Limit Exceeded";
        errorMessage = error.message;
        errorType = "rate_limit";
      } else if (error.message.includes("not available for self-service")) {
        errorTitle = "Access Restricted";
        errorMessage = `Feature "${
          currentFeature?.displayName || featureKey
        }" cannot be modified through self-service`;
        errorType = "access_restricted";
      } else if (error.message.includes("Authentication required")) {
        errorTitle = "Authentication Required";
        errorMessage = "Please log in again to continue";
        errorType = "authentication_required";
      } else if (error.message.includes("Access denied")) {
        errorTitle = "Permission Denied";
        errorMessage = "You do not have permission to modify feature flags";
        errorType = "permission_denied";
      } else if (error.message.includes("invalid characters")) {
        errorTitle = "Invalid Feature";
        errorMessage = "Feature name contains invalid characters";
        errorType = "invalid_feature";
      } else if (error.message.includes("Server error")) {
        errorTitle = "Server Error";
        errorMessage =
          "Server temporarily unavailable. Please try again later.";
        errorType = "server_error";
      } else {
        errorMessage = `${errorMessage}: ${error.message}`;
        errorType = "unknown";
      }

      // Track toggle error
      trackProfileFeatureToggleError(featureKey, newValue, {
        errorType,
        errorMessage,
        featureDisplayName: currentFeature?.displayName || featureKey
      });

      // Show error toast with enhanced messaging
      setToasts((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "error",
          title: errorTitle,
          message: errorMessage,
        },
      ]);

    }
  };

  /**
   * Remove a toast by ID
   */
  const removeToast = (toastId) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  };

  /**
   * Reset all features to default state (disabled)
   */
  const handleResetToDefaults = async () => {
    const enabledFeatures = availableFeatures.filter((f) => f.enabled);

    if (enabledFeatures.length === 0) {
      return;
    }

    // Optimistic update
    setAvailableFeatures((prev) =>
      prev.map((feature) => ({
        ...feature,
        enabled: false,
      }))
    );

    // Reset each enabled feature via API
    const resetPromises = enabledFeatures.map((feature) =>
      retryApiCall(() => setMyFeatureFlag(feature.name, false)).catch(
        (error) => ({ feature: feature.name, error })
      )
    );

    try {
      const results = await Promise.allSettled(resetPromises);
      const failures = results
        .filter((result) => result.status === "rejected" || result.value?.error)
        .map((result) => result.value?.feature || "unknown");

      if (failures.length > 0) {
        // Partial failure - revert failed features
        setAvailableFeatures((prev) =>
          prev.map((feature) => ({
            ...feature,
            enabled: failures.includes(feature.name) ? true : false,
          }))
        );

        setToasts((prev) => [
          ...prev,
          {
            id: Date.now(),
            type: "warning",
            title: "Partial Reset",
            message: `${
              enabledFeatures.length - failures.length
            } features reset successfully, ${failures.length} failed`,
          },
        ]);

        // Track partial success
        trackProfileFeatureReset({
          totalFeatures: enabledFeatures.length,
          successCount: enabledFeatures.length - failures.length,
          failureCount: failures.length,
          failedFeatures: failures,
          success: false
        });
      } else {
        // Track complete success
        trackProfileFeatureReset({
          totalFeatures: enabledFeatures.length,
          successCount: enabledFeatures.length,
          failureCount: 0,
          success: true
        });
      }
    } catch (error) {
      // Complete failure - revert all changes
      setAvailableFeatures((prev) =>
        prev.map((feature) => ({
          ...feature,
          enabled: enabledFeatures.find((ef) => ef.name === feature.name)
            ? true
            : feature.enabled,
        }))
      );

      setToasts((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "error",
          title: "Reset Failed",
          message: "Failed to reset feature flags. Please try again.",
        },
      ]);

      // Track complete failure
      trackProfileFeatureReset({
        totalFeatures: enabledFeatures.length,
        successCount: 0,
        failureCount: enabledFeatures.length,
        success: false
      });
    }
  };

  /**
   * Handle search term changes with tracking
   */
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    // Track search if user has typed something
    if (newSearchTerm.length > 0) {
      const results = availableFeatures.filter(
        (feature) =>
          feature.displayName.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
          feature.description.toLowerCase().includes(newSearchTerm.toLowerCase()) ||
          feature.category.toLowerCase().includes(newSearchTerm.toLowerCase())
      );

      trackProfileFeatureSearch(newSearchTerm, results.length, {
        totalFeatures: availableFeatures.length
      });
    }
  };

  /**
   * Filter features based on search term
   */
  const filteredFeatures = availableFeatures.filter(
    (feature) =>
      feature.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      feature.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Group features by category
   */
  const groupedFeatures = filteredFeatures.reduce((groups, feature) => {
    const category = feature.category || "other";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(feature);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading feature flags...
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        variant="danger"
        className="mb-0 d-flex align-items-center justify-content-between"
      >
        <div>
          <Info size={16} className="me-2" />
          {error}
        </div>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => initializeFeatures(false)}
        >
          <RotateCcw size={16} className="me-1" />
          Retry
        </Button>
      </Alert>
    );
  }

  if (availableFeatures.length === 0) {
    return (
      <Alert variant="info" className="mb-0">
        <Info size={16} className="me-2" />
        No feature flags are currently available for management.
      </Alert>
    );
  }

  return (
    <>
      <div className="feature-flag-section">
        {/* Header with instructions */}
        <Row className="mb-4">
          <Col>
            <p className="text-muted mb-0">
              Manage your personal feature flags below. Changes take effect
              immediately.
            </p>
          </Col>
        </Row>

        {/* Search and filter controls */}
        {availableFeatures.length > 2 && (
          <Row className="mb-4">
            <Col md={8}>
              <div className="position-relative">
                <Search
                  size={18}
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                  style={{ pointerEvents: 'none' }}
                />
                <input
                  type="text"
                  className="form-control feature-search-input ps-5"
                  placeholder="Search feature flags..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </Col>
          </Row>
        )}

        {/* Feature flags grouped by category */}
        {Object.keys(groupedFeatures).map((category) => (
          <div key={category} className="mb-4">
            {Object.keys(groupedFeatures).length > 1 && (
              <h6 className="text-muted mb-3">
                {category} Features
              </h6>
            )}

            {groupedFeatures[category].map((feature) => (
              <SelfServiceFeatureToggle
                key={feature.name}
                feature={feature}
                enabled={feature.enabled}
                onChange={handleFeatureToggle}
                disabled={false}
                loading={loading}
              />
            ))}
          </div>
        ))}

        {/* No results message */}
        {filteredFeatures.length === 0 && searchTerm && (
          <Alert variant="info" className="text-center">
            <Info size={16} className="me-2" />
            No feature flags match your search: "{searchTerm}"
          </Alert>
        )}

        {/* Help text */}
        <Row>
          <Col>
            <Alert variant="secondary" className="mb-0">
              <small>
                <strong>Need help?</strong> Feature flags allow you to enable or
                disable specific functionality. Experimental features may have
                limited support. Changes are saved automatically and will
                persist across sessions.
              </small>
            </Alert>
          </Col>
        </Row>

        {/* Danger Zone - Destructive Actions */}
        <Row className="mt-5">
          <Col>
            <div className="danger-zone" role="region" aria-label="Danger Zone">
              <h6 className="danger-zone-title">Danger Zone</h6>
              <div className="danger-zone-item">
                <div>
                  <h6 className="mb-1">Reset all feature flags</h6>
                  <p id="reset-description" className="text-muted small mb-0">
                    Disable all feature flags and restore to defaults. This action cannot be undone.
                  </p>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleResetToDefaults}
                  aria-describedby="reset-description"
                  title="Reset all features to default (disabled) state"
                >
                  <RotateCcw size={16} className="me-1" />
                  Reset to Defaults
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Toast notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '1rem', right: '1rem' }}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            show={true}
            onClose={() => removeToast(toast.id)}
            delay={3000}
            autohide
            bg={
              toast.type === "success"
                ? "success"
                : toast.type === "error"
                ? "danger"
                : toast.type === "warning"
                ? "warning"
                : "info"
            }
          >
            <Toast.Header>
              <strong className="me-auto">{toast.title}</strong>
            </Toast.Header>
            <Toast.Body
              className={toast.type === "success" ? "text-white" : ""}
            >
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </>
  );
}

export default ProfileFeatureManager;
